import { useFetch, useSubmit, useListener } from "@hyper-fetch/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { client } from "@/api/client";
import { sdk, socketSdk } from "@/api/sdk";
import type { WsNotificationPayload } from "@complete-athlete/backend";

export const useNotificationDropdown = (status: "all" | "unread" = "all") => {
  const dropdownRequest = useMemo(() => sdk.notifications.$get.setQueryParams({ limit: "10", status }), [status]);
  const { data, loading, error, refetch } = useFetch(dropdownRequest, {
    revalidate: false,
  });

  const [recentItems, setRecentItems] = useState<WsNotificationPayload[]>([]);

  useEffect(() => {
    if (data?.data) {
      setRecentItems(data.data as WsNotificationPayload[]);
    }
  }, [data]);

  const newListener = useListener(socketSdk.notification.new.$listener);

  newListener.onEvent(({ data: notification }) => {
    setRecentItems((prev) => [notification, ...prev.filter((item) => item.id !== notification.id)].slice(0, 10));
  });

  const { submit: markAllRead, submitting: markingAllRead } = useSubmit(sdk.notifications.readAll.$post);

  const handleMarkAllRead = useCallback(async () => {
    const { error: mutationError } = await markAllRead({});
    if (mutationError) return false;
    setRecentItems((prev) => prev.map((n) => ({ ...n, readAt: new Date().toISOString() })));
    const cache = client.cache;
    cache.invalidate(/\/notifications/);
    await refetch();
    return true;
  }, [markAllRead, refetch]);

  const handleMarkRead = useCallback(async (notificationId: string) => {
    const req = sdk.notifications.$notificationId.markRead.$patch.setParams({ notificationId });
    const { error: mutationError } = await req.send();
    if (mutationError) return false;
    setRecentItems((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n)),
    );
    client.cache.invalidate(/\/notifications/);
    return true;
  }, []);

  return {
    items: recentItems,
    loading,
    error,
    markingAllRead,
    handleMarkAllRead,
    handleMarkRead,
    refetch,
  };
};
